import * as THREE from "three";

const NAVY = 0x07111f;
const BLUE = 0x3b9aef;
const SKY = 0x9fd0ff;

export function mountBootWarp(host: HTMLElement): () => void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.setClearColor(NAVY, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(NAVY, 0.045);
  const camera = new THREE.PerspectiveCamera(68, 1, 0.1, 80);
  camera.position.set(0, 0, 6);

  const count = reduced ? 240 : 1600;
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const speeds = new Float32Array(count);
  const blue = new THREE.Color(BLUE);
  const sky = new THREE.Color(SKY);
  const white = new THREE.Color(0xf4f8ff);
  for (let i = 0; i < count; i += 1) {
    placeStar(positions, speeds, i);
    const tint = i % 7 === 0 ? white : i % 3 === 0 ? sky : blue;
    colors.set([tint.r, tint.g, tint.b], i * 3);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const stars = new THREE.Points(geometry, new THREE.PointsMaterial({
    size: reduced ? 0.045 : 0.055,
    vertexColors: true,
    transparent: true,
    opacity: 0.92,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  }));
  scene.add(stars);

  const rings = new THREE.Group();
  for (let i = 0; i < 7; i += 1) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.15 + i * 0.08, 0.008, 8, 80),
      new THREE.MeshBasicMaterial({ color: i % 2 === 0 ? BLUE : SKY, transparent: true, opacity: 0.55 }),
    );
    ring.position.z = -i * 4.2;
    rings.add(ring);
  }
  scene.add(rings);

  let frame = 0;
  let stopped = false;
  const resize = () => {
    const width = host.clientWidth || window.innerWidth;
    const height = host.clientHeight || window.innerHeight;
    camera.aspect = width / Math.max(height, 1);
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  };
  resize();
  const observer = new ResizeObserver(resize);
  observer.observe(host);

  const tick = () => {
    if (stopped) return;
    frame = window.requestAnimationFrame(tick);
    if (!reduced) {
      const pos = geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < count; i += 1) {
        pos[i * 3 + 2] += speeds[i];
        if (pos[i * 3 + 2] > 8) placeStar(pos, speeds, i, -46);
      }
      geometry.attributes.position.needsUpdate = true;
      rings.children.forEach((ring, index) => {
        ring.position.z += 0.18;
        if (ring.position.z > 8) ring.position.z = -22 - index;
        ring.rotation.z += 0.002;
      });
      camera.position.x = Math.sin(performance.now() * 0.00035) * 0.18;
      camera.position.y = Math.cos(performance.now() * 0.00028) * 0.1;
      camera.lookAt(0, 0, -8);
    }
    renderer.render(scene, camera);
  };
  tick();

  return () => {
    stopped = true;
    window.cancelAnimationFrame(frame);
    observer.disconnect();
    geometry.dispose();
    (stars.material as THREE.Material).dispose();
    rings.children.forEach((ring) => {
      (ring as THREE.Mesh).geometry.dispose();
      ((ring as THREE.Mesh).material as THREE.Material).dispose();
    });
    renderer.dispose();
    renderer.domElement.remove();
  };
}

function placeStar(positions: Float32Array, speeds: Float32Array, index: number, z?: number) {
  const angle = Math.random() * Math.PI * 2;
  const radius = 0.35 + Math.random() ** 0.55 * 7.5;
  positions[index * 3] = Math.cos(angle) * radius;
  positions[index * 3 + 1] = Math.sin(angle) * radius * 0.62;
  positions[index * 3 + 2] = z ?? -Math.random() * 52;
  speeds[index] = 0.16 + Math.random() * 0.55;
}
